import { apiClient } from '@lib/api/client';
import type { CertificateListResponse } from '../types/certificate.types';

export const certificateService = {
  async getCertificates(): Promise<CertificateListResponse> {
    console.log('🔵 certificateService.getCertificates - Calling API...');
    const response = await apiClient.get<any>('/companies/client-certificates');
    console.log('✅ certificateService.getCertificates - Response:', response.data);

    // Backend returns { hasCertificates, certificates: [...sanitized], status }.
    // We expose only the first certificate (we never have more than one per company).
    const certificate = response.data.certificates?.[0] || null;
    return { certificate } as CertificateListResponse;
  },

  /**
   * Upload certificates to PAC (DGI)
   * Converts .pfx/.p12 files to base64 and extracts names
   */
  async uploadCertificates(data: {
    authCertFile: File;
    signCertFile: File;
    secretJwt: string | null;
    certificatePassword: string;
    secretDGI?: string;
  }): Promise<any> {
    // Helper function to convert file to base64
    const fileToBase64 = (file: File): Promise<string> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          // Remove the data:application/x-pkcs12;base64, prefix
          const base64 = (reader.result as string).split(',')[1];
          resolve(base64);
        };
        reader.onerror = error => reject(error);
      });
    };

    // Helper function to extract certificate name (without extension)
    const extractCertName = (filename: string): string => {
      return filename.replace(/\.(pfx|p12)$/i, '');
    };

    try {
      console.log('Converting certificates to base64...');

      const [authBase64, signBase64] = await Promise.all([
        fileToBase64(data.authCertFile),
        fileToBase64(data.signCertFile),
      ]);

      const payload = {
        authCertificate: {
          name: extractCertName(data.authCertFile.name),
          base64: authBase64,
        },
        signCertificate: {
          name: extractCertName(data.signCertFile.name),
          base64: signBase64,
        },
        secretJwt: data.secretJwt,
        certificatePassword: data.certificatePassword,
        secretDGI: data.secretDGI || null,
      };

      console.log('Uploading certificates...', {
        authName: payload.authCertificate.name,
        signName: payload.signCertificate.name,
      });

      const response = await apiClient.post('/certificates/upload', payload);

      console.log('✅ Certificates uploaded successfully');
      return response.data;
    } catch (error) {
      console.error('❌ Error uploading certificates:', error);
      throw error;
    }
  },
};
